import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Center,
  type ChatConversation,
  ChatView,
  Chip,
  Container,
  Flex,
  Placeholder,
  ResponseBody,
  Reveal,
  Stack,
  useChatHistory,
  useFormattedDate,
  useOnce,
} from "@huub-web/ui";
import { rem } from "@huub-web/utils";
import { Section } from "@huub-web/shared";

import { useAppController } from "../../hooks/useAppController";
import { usePageReady } from "../../hooks/usePageReady";

import { containerStyle } from "./HistoryScreen.css";

type HistoryScreenProps = {
  leading?: ReactNode;
  chatID: string;
};

/**
 * The HistoryScreen component is used to displays a historic conversation.
 */
export const HistoryScreen: FC<HistoryScreenProps> = ({ chatID, leading }) => {
  const { fetchConversation } = useChatHistory();
  const { t } = useTranslation();
  const {
    activateCustomLoadingState,
    deactivateCustomLoadingState,
    clearChat,
  } = useAppController();
  const { formatDate } = useFormattedDate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [chat, setChat] = useState<ChatConversation | null>(null);
  const loadedChatRef = useRef<string | null>(null);
  const isLoadingRef = useRef(false);

  usePageReady({ loading });

  useOnce(() => {
    clearChat();
  });

  useEffect(() => {
    if (!chatID || isLoadingRef.current || loadedChatRef.current === chatID) {
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);
    setError(false);

    fetchConversation(chatID)
      .then((chat) => {
        clearChat();
        setChat(chat);
      })
      .catch((error) => {
        setError(true);
        console.error(error);
      })
      .finally(() => {
        isLoadingRef.current = false;
        setLoading(false);
        loadedChatRef.current = chatID;
      });
  }, [chatID, loading]);

  useEffect(() => {
    if (loading) {
      activateCustomLoadingState();
    } else {
      deactivateCustomLoadingState();
    }
  }, [loading]);

  return (
    <Section>
      <Flex direction="column" className={containerStyle}>
        {leading}

        <Container>
          <Center>
            {chat?.created && (
              <Chip variant="secondary">
                {formatDate(chat.created, { format: "dayMonthYear" })}
              </Chip>
            )}
          </Center>

          <Stack data-stack="history">
            <Reveal>
              {loading && !error && (
                <Flex direction="column" gap="m">
                  <Flex justify="flex-start" direction="column" gap="s">
                    <Placeholder
                      variant="loading"
                      height={rem(26)}
                      width="30%"
                    />
                    <Placeholder
                      variant="loading"
                      height={rem(100)}
                      width="60%"
                    />
                  </Flex>
                  <Flex
                    justify="flex-end"
                    direction="column"
                    align="flex-end"
                    gap="s"
                  >
                    <Placeholder
                      variant="loading"
                      height={rem(26)}
                      width="30%"
                    />
                    <Placeholder
                      variant="loading"
                      height={rem(100)}
                      width="60%"
                    />
                  </Flex>
                  <Flex justify="flex-start" direction="column" gap="s">
                    <Placeholder
                      variant="loading"
                      height={rem(26)}
                      width="30%"
                    />
                    <Placeholder
                      variant="loading"
                      height={rem(100)}
                      width="60%"
                    />
                  </Flex>
                </Flex>
              )}
            </Reveal>

            <Reveal>
              {error && !loading && (
                <Container>
                  <ResponseBody content={t("general.error")} />
                </Container>
              )}
            </Reveal>

            <Reveal>
              {chat && !loading && !error && (
                <ChatView showTime showAuthor conversation={chat} />
              )}
            </Reveal>
          </Stack>
        </Container>
      </Flex>
    </Section>
  );
};
